import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ApplicantInfosResponse } from '~/api/queries/applicant'
import { type ApiResponseError, fetchApiWithToken } from '~/api/utils'
import {
  type OptimisticEventContext,
  useAddOptimisticHistoryEvent,
  useRollbackOptimisticHistoryEvents
} from '~/domains/actions/hooks/optimisticHistoryUpdates'
import { generateStatusUpdateEvent } from '~/domains/actions/utils/historyEventGenerator'
import type { TaxonomyItem } from '~/domains/taxonomies/types'

type UpdateApplicantStatusPayload = {
  status: TaxonomyItem<'statuses'>
  archiveType?: TaxonomyItem<'archive_types'>
}

export const useUpdateApplicantStatusMutation = (applicantId: number) => {
  const queryClient = useQueryClient()
  const addOptimisticHistoryEvent = useAddOptimisticHistoryEvent()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()

  return useMutation<
    UpdateApplicantStatusPayload,
    ApiResponseError,
    UpdateApplicantStatusPayload,
    {
      oldApplicant?: ApplicantInfosResponse
      optimisticEvent?: OptimisticEventContext
    }
  >({
    mutationFn: ({ status, archiveType }) =>
      fetchApiWithToken({
        method: 'put',
        url: `/v1/applicants/${applicantId}`,
        data: {
          status: `/v1/taxonomies/statuses/${status.id}`,
          ...(archiveType !== undefined && {
            archiveType: `/v1/taxonomies/archive_types/${archiveType.id}`
          })
        }
      }),
    onMutate: ({ status, archiveType }) => {
      void queryClient.cancelQueries({ queryKey: ['applicants', applicantId] })

      // Optimistic update for applicant data
      const oldApplicant = queryClient.getQueryData<ApplicantInfosResponse>([
        'applicants',
        applicantId
      ])
      queryClient.setQueryData(['applicants', applicantId], {
        ...oldApplicant,
        status
      })

      // Optimistic update for applicant history
      const optimisticEvent = generateStatusUpdateEvent([applicantId], {
        status,
        archiveType
      })

      const optimisticEventContext = addOptimisticHistoryEvent(
        applicantId,
        optimisticEvent
      )

      return {
        oldApplicant,
        optimisticEvent: optimisticEventContext
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['applicants', applicantId]
      })
      void queryClient.invalidateQueries({
        queryKey: ['applicant', applicantId, 'history']
      })
      void queryClient.invalidateQueries({ queryKey: ['order'] })
    },
    onError: (_, __, context) => {
      // Rollback applicant data
      queryClient.setQueryData(
        ['applicants', applicantId],
        context?.oldApplicant
      )

      // Rollback history data
      if (context?.optimisticEvent) {
        rollbackOptimisticHistoryEvents([context.optimisticEvent])
      }
    }
  })
}
